import React, { useEffect, useState } from 'react';
import { fetchPendingMappings, confirmMapping, fetchEmployees } from '../lib/api';

interface Employee {
  id: string;
  displayName: string;
  legalName: string;
  rut: string | null;
  area: string | null;
}

interface PendingMapping {
  id: string;
  rawName: string;
  source: string;
  confidence: number | null;
  employeeId: string;
  bestCandidate: {
    employeeId: string;
    displayName: string;
    score: number;
  } | null;
}

interface MappingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResolved: () => void;
}

export function MappingModal({ isOpen, onClose, onResolved }: MappingModalProps) {
  const [pending, setPending] = useState<PendingMapping[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setIsLoading(true);
    Promise.all([fetchPendingMappings(), fetchEmployees()])
      .then(([mappingsData, employeesData]) => {
        setPending(mappingsData.pending);
        // En el demo no hay backend de mapeo; fetchEmployees devuelve Employee[].
        setEmployees(employeesData as unknown as Employee[]);
        const initialSelections: Record<string, string> = {};
        mappingsData.pending.forEach((m: PendingMapping) => {
          initialSelections[m.rawName] = m.bestCandidate?.employeeId || m.employeeId || '';
        });
        setSelections(initialSelections);
      })
      .finally(() => setIsLoading(false));
  }, [isOpen]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      for (const mapping of pending) {
        const employeeId = selections[mapping.rawName];
        if (employeeId) {
          await confirmMapping(mapping.rawName, employeeId, mapping.source);
        }
      }
      onResolved();
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">Mapear nombres</h2>
          <p className="text-slate-600 text-sm mt-1">
            Asigna cada nombre del archivo CSV a un colaborador del sistema.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="text-center py-8 text-slate-600">Cargando...</div>
          ) : pending.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No hay nombres pendientes por mapear.
            </div>
          ) : (
            <div className="space-y-4">
              {pending.map((mapping) => (
                <div key={mapping.id} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-slate-900">{mapping.rawName}</span>
                    {mapping.confidence !== null && (
                      <span className="text-xs text-slate-500">
                        Confianza sugerida: {Math.round(mapping.confidence * 100)}%
                      </span>
                    )}
                  </div>
                  <select
                    value={selections[mapping.rawName] || ''}
                    onChange={(e) => setSelections({ ...selections, [mapping.rawName]: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-fycom-blue outline-none"
                  >
                    <option value="">Seleccionar colaborador...</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.displayName} {emp.legalName ? `(${emp.legalName})` : ''}
                      </option>
                    ))}
                  </select>
                  {mapping.bestCandidate && (
                    <div className="mt-2 text-xs text-slate-500">
                      Sugerencia: {mapping.bestCandidate.displayName} ({Math.round(mapping.bestCandidate.score * 100)}%)
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition"
          >
            Cerrar
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || pending.length === 0}
            className="px-4 py-2 bg-fycom-navy text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 transition"
          >
            {isSaving ? 'Guardando...' : 'Guardar mapeos'}
          </button>
        </div>
      </div>
    </div>
  );
}
